import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import "./css/Blogstyles.css";
import {
  fetchPostBySlug,
  clearSelectedPost,
  fetchPosts,
} from "../redux/postSlice";
import {
  fetchInteractionData,
  resetInteraction,
} from "../redux/interactionSlice";

function BlogDetail() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.currentUser);
  const post = useSelector((state) => state.posts.selectedPost);
  const { posts } = useSelector((state) => state.posts);
  const loading = useSelector((state) => state.posts.loading);

  const [readingTime, setReadingTime] = useState(0);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    dispatch(fetchPostBySlug(slug));
    dispatch(fetchPosts({ page: 1, limit: 1000 }));
    return () => {
      dispatch(clearSelectedPost());
    };
  }, [dispatch, slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
    if (post?._id) {
      dispatch(fetchInteractionData({ postId: post._id, userId: user?._id }));
      calculateReadingTime(post.content);
      findRelatedPosts();
    } else {
      dispatch(resetInteraction());
      setRelatedPosts([]);
      setReadingTime(0);
    }
  }, [post?._id, user?._id, posts, dispatch]);

  // Okuma süresi hesaplama
  const calculateReadingTime = (content) => {
    if (!content) return;
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, "");
    const wordCount = text.split(/\s+/).length;
    const time = Math.ceil(wordCount / wordsPerMinute);
    setReadingTime(time);
  };

  // Benzer yazıları bulma
  const findRelatedPosts = () => {
    if (post && posts && posts.length > 0) {
      const related = posts
        .filter((p) => p._id !== post._id && p.category === post.category)
        .slice(0, 3);
      setRelatedPosts(related);
    }
  };

  // Sosyal medya paylaşım fonksiyonları
  const shareOnFacebook = () => {
    const url = `https://www.dilanaslan.com/blog/${slug}`;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank",
      "width=600,height=400"
    );
  };

  const shareOnTwitter = () => {
    const url = `https://www.dilanaslan.com/blog/${slug}`;
    const text = post?.title || "";
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(text)}`,
      "_blank",
      "width=600,height=400"
    );
  };

  const shareOnWhatsApp = () => {
    const url = `https://www.dilanaslan.com/blog/${slug}`;
    const text = `${post?.title || ""} - ${url}`;
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
      "_blank"
    );
  };

  if (loading) {
    return (
      <div className="blogdetail-loading-container">
        <div className="blogdetail-spinner"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blogdetail-notfound-container">
        <h4>Yazı bulunamadı.</h4>
        <p>Aradığınız içerik yayından kaldırılmış olabilir.</p>
      </div>
    );
  }

  const formattedDate = post?.date
    ? new Date(post.date).toLocaleDateString("tr-TR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <section className="blogdetail-section">
      <Helmet>
        <title>{post.title} | Dilan Aslan</title>
        <meta
          name="description"
          content={post.excerpt || post.content?.slice(0, 160)}
        />
        <meta name="keywords" content={post.tags?.join(", ")} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:image" content={post.image} />
        <meta
          property="og:url"
          content={`https://www.dilanaslan.com/blog/${slug}`}
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="blogdetail-container">
        <article className="blogdetail-article">
          <header className="blogdetail-header">
            <h1 className="blogdetail-title">{post.title}</h1>
            <div className="blogdetail-meta">
              {formattedDate && (
                <>
                  <span className="blogdetail-date">{formattedDate}</span>
                  <span className="blogdetail-separator">•</span>
                </>
              )}
              <span className="blogdetail-reading-time">
                {readingTime} dk okuma süresi
              </span>
            </div>
          </header>

          {post.image && (
            <div className="blogdetail-image-wrapper">
              <img
                src={post.image}
                alt={post.title}
                className="blogdetail-image"
              />
            </div>
          )}

          <div
            className="blogdetail-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Sosyal Medya Paylaşım Butonları */}
          <div className="blogdetail-share-section">
            <h3 className="blogdetail-share-title">Bu yazıyı paylaş</h3>
            <div className="blogdetail-share-buttons">
              <button
                onClick={shareOnFacebook}
                className="blogdetail-share-btn blogdetail-share-facebook"
                aria-label="Facebook'ta paylaş"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Facebook
              </button>

              <button
                onClick={shareOnTwitter}
                className="blogdetail-share-btn blogdetail-share-twitter"
                aria-label="Twitter'da paylaş"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
                Twitter
              </button>

              <button
                onClick={shareOnWhatsApp}
                className="blogdetail-share-btn blogdetail-share-whatsapp"
                aria-label="WhatsApp'ta paylaş"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                WhatsApp
              </button>
            </div>
          </div>
        </article>

        {/* Benzer Yazılar */}
        {relatedPosts && relatedPosts.length > 0 && (
          <section className="blogdetail-related-section">
            <h2 className="blogdetail-related-title">Benzer Yazılar</h2>
            <div className="blogdetail-related-grid">
              {relatedPosts.map((relatedPost) => (
                <article
                  key={relatedPost._id}
                  className="blogdetail-related-card"
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                >
                  <div className="blogdetail-related-image-wrapper">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="blogdetail-related-image"
                    />
                  </div>
                  <div className="blogdetail-related-content">
                    <h3 className="blogdetail-related-card-title">
                      {relatedPost.title}
                    </h3>
                    {relatedPost.date && (
                      <p className="blogdetail-related-date">
                        {new Date(relatedPost.date).toLocaleDateString(
                          "tr-TR",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </section>
  );
}

export default BlogDetail;
